import type React from 'react';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';

import Modal from '@molecules/Modal';
import TeamSwitchWarning from '@components/SATeams/TeamSwitchWarning';
import Loader from '@atoms/Atom/Loader';

import { getAuth } from '@services/identity.service';
import { usePurchasePlan } from '@lib/hooks/usePlanSettings';
import { useOffice } from '@components/OfficeLoader';
import { useSyncedLocalStorage } from '@lib/hooks/useSyncedLocalStorage';
import { useRealtimeSync } from '@lib/realtimeSync/useRealtimeSync';

import { useTeamsStore, AUTH_STATE_KEY, IDLE_AUTH_STATE, type AuthStateMessage } from '@store/teamsStore';

/**
 * TeamsGate — replaces the old TeamsDataProvider context.
 *
 * Responsibilities:
 *  - Triggers initTeam() on mount
 *  - Syncs usePurchasePlan settings into the store
 *  - Handles the PowerPoint cross-tab "login" event (logout is handled in the store)
 *  - Renders the loading gate and team-switch modal
 *  - Does NOT use React Context — state lives in useTeamsStore
 */
export function TeamsGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { isPowerpoint, getLastRoutePath } = useOffice();

  const {
    isLoading,
    isClient,
    teamContext,
    modal,
    newTeam,
    toggleModal,
    switchTeam,
    setNewTeam,
    setIsClient,
    initTeam,
    currentTeamBillingAccountOwnerID,
  } = useTeamsStore();

  const { settings } = usePurchasePlan(currentTeamBillingAccountOwnerID ?? '');

  // Publish this app instance's presence into the realtime sync control plane.
  useRealtimeSync();

  // Sync billing plan settings into the store whenever they change
  useEffect(() => {
    useTeamsStore.setState({ settings });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(settings)]);

  // Bootstrap: mark client-side and load teams/user
  useEffect(() => {
    setIsClient(true);
    initTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle cross-tab "login" event (PowerPoint re-auth only)
  const [authState] = useSyncedLocalStorage<AuthStateMessage>(AUTH_STATE_KEY, IDLE_AUTH_STATE);
  // Guarded on `at` — a uniquifier stamped on every write — so this only reacts
  // to writes made *after* mount, not to a stale "login" already sitting in
  // localStorage from a previous session. Baseline is taken from the first
  // value this effect actually observes (`null` until then), not from the
  // component's first render: that first render is server/pre-hydration and
  // always reports IDLE_AUTH_STATE regardless of what's really stored, so
  // seeding the ref eagerly would treat the post-hydration sync-up as a fresh
  // write and misfire the re-auth flow.
  const authStateAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (authStateAtRef.current === null) {
      authStateAtRef.current = authState.at;
      return;
    }
    if (authState.at === authStateAtRef.current) return;
    authStateAtRef.current = authState.at;
    if (authState.event !== 'login' || !isPowerpoint) return;

    (async () => {
      await initTeam();
      if (pathname?.includes('/connect-to-stream')) return;
      const lastRoute = await getLastRoutePath();
      router.push(lastRoute);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.at]);

  if (!isClient) return null;

  const auth = getAuth();

  const isEmbedRoute = router.pathname.startsWith('/embed/') || router.pathname === '/oauth/authorize';
  if ((isLoading || (auth && !teamContext?.oid)) && !isEmbedRoute) {
    if (['sandbox'].includes(process.env.NEXT_PUBLIC_APP_ENV ?? '')) return null;
    return (
      <div className="h-screen">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Loader
          backgroundColorWrapper="bg-content2"
          className={isPowerpoint ? 'fixed w-full' : ''}
        />
      </div>
    );
  }

  return (
    <>
      {children}
      {modal && (
        <Modal className="">
          <TeamSwitchWarning
            onCancel={toggleModal}
            onSuccess={() => {
              if (newTeam) switchTeam({ ...newTeam });
              setNewTeam(undefined);
              toggleModal();
              router.push('/');
            }}
          />
        </Modal>
      )}
    </>
  );
}
